/**
 * Lokal cache av aktiva ärenden så att fuzzy-sök i add-in:en sker utan
 * roundtrip per tangenttryck. Speglar /api/cases/for-filing/.
 *
 * TTL: 5 min. Vid läs ut bortom TTL revalideras i bakgrunden men det
 * cachade värdet returneras direkt så UI:t inte blockerar.
 */

import { fetchCasesForFiling, filingCaseToSigge, type FilingCase, type SiggeCase } from "./api";

const CASES_KEY = "sigge_cases_for_filing";
const TTL_MS = 5 * 60 * 1000;

interface CacheRecord {
  fetched_at: number;
  cases: FilingCase[];
}

function readRaw(): CacheRecord | null {
  const raw = sessionStorage.getItem(CASES_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CacheRecord;
  } catch {
    return null;
  }
}

export const casesCache = {
  /** Returnerar cachade ärenden om de finns, oavsett om de är gamla. */
  peek(): FilingCase[] | null {
    return readRaw()?.cases ?? null;
  },

  /** True om cachen är yngre än TTL. */
  isFresh(): boolean {
    const rec = readRaw();
    if (!rec) return false;
    return Date.now() - rec.fetched_at < TTL_MS;
  },

  write(cases: FilingCase[]): void {
    const rec: CacheRecord = { fetched_at: Date.now(), cases };
    try {
      sessionStorage.setItem(CASES_KEY, JSON.stringify(rec));
    } catch {
      /* sessionStorage kan vara full eller blockerad — ignorera */
    }
  },

  clear(): void {
    sessionStorage.removeItem(CASES_KEY);
  },
};

/**
 * Hämtar från cache om färsk, annars går mot API. Vid stale cache:
 * returnera stale-värdet direkt och revalidera i bakgrunden.
 */
export async function loadActiveCases(): Promise<FilingCase[]> {
  const stale = casesCache.peek();
  if (casesCache.isFresh() && stale) return stale;

  if (stale) {
    void fetchCasesForFiling()
      .then((fresh) => casesCache.write(fresh))
      .catch(() => {
        /* tysta — vi har stale-värdet */
      });
    return stale;
  }

  const fresh = await fetchCasesForFiling();
  casesCache.write(fresh);
  return fresh;
}

/** Lokal fuzzy-sök mot cachen. Returnerar SiggeCase-shape för UI. */
export function localSearchCases(
  cases: FilingCase[],
  query: string,
  limit = 20
): SiggeCase[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const tokens = q.split(/\s+/);
  const scored: { c: FilingCase; score: number }[] = [];
  for (const c of cases) {
    const hay =
      `${c.title} ${c.client_email} ${c.client_name} ${c.customer_name} #${c.id}`.toLowerCase();
    let score = 0;
    for (const t of tokens) {
      if (!hay.includes(t)) {
        score = -1;
        break;
      }
      // Liten bonus om träffen är på titel (viktigare än customer/client)
      if (c.title.toLowerCase().includes(t)) score += 2;
      else score += 1;
    }
    if (score > 0) scored.push({ c, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => filingCaseToSigge(s.c));
}

// ---------------------------------------------------------------------------
// Outlook-kategori: markera arkiverat mejl i mejllistan
// ---------------------------------------------------------------------------

const FILED_CATEGORY = "Sigge: arkiverat";

function ensureMasterCategory(): Promise<void> {
  return new Promise((resolve) => {
    const mc = Office.context.mailbox.masterCategories;
    if (!mc) return resolve();
    mc.getAsync((listResult) => {
      if (listResult.status !== Office.AsyncResultStatus.Succeeded) {
        return resolve();
      }
      const exists = (listResult.value || []).some(
        (cat) => cat.displayName === FILED_CATEGORY
      );
      if (exists) return resolve();
      mc.addAsync(
        [
          {
            displayName: FILED_CATEGORY,
            color: Office.MailboxEnums.CategoryColor.Preset9,
          },
        ],
        () => resolve()
      );
    });
  });
}

/**
 * Sätter kategorin "Sigge: arkiverat" på det öppna mejlet så att det syns
 * i mejllistan. Tyst no-op om Office.js inte stödjer det (äldre klienter)
 * eller om API:t inte är tillgängligt för aktuellt mail-item.
 */
export async function markFiledInOutlook(): Promise<void> {
  const item = Office.context.mailbox.item;
  if (!item) return;
  const categories = (item as Office.MessageRead).categories;
  if (!categories || typeof categories.addAsync !== "function") return;

  try {
    await ensureMasterCategory();
  } catch {
    return;
  }

  await new Promise<void>((resolve) => {
    categories.addAsync([FILED_CATEGORY], () => resolve());
  });
}
